import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap'

import SoftCard from '../../SoftCard';
import { SubscriptionInfo } from '../SubscriptionInfo/SubscriptionInfo';

import styles from './AllSubscriptions.module.scss'

/**
 * 
 * @param {array} subscriptions - array of objects 
 * @param {int} showSingleTypeId - if set, will only show subscriptions of the passed in type id, works well for displaying all of only a single type of subscriptions
 * @param {boolean} showSingleNonActive - if true, will only show one non-active subscription, works when viewing all
 * @param {boolean} showAmount - if true, will show the amount of other subscriptions if there are any - will also allow for an option to display all those, too
 * @param {boolean} showNoSubsOfType - if true, will show a message if there are no subscriptions of the type
 * @param {boolean} showDates - if true, will show the start and end dates of the subscription
 * @param {boolean} showTitle - if true, will show the title "SUBSCRIPTIONS"
 * @param {string} maxWidth - if set, will set the max width of the container; it will not override the max-width of the individual 
 * @param {boolean} border - if true, will add a border around each subscription
 * @param {boolean} col - if it's true, the cards will be in a column and if it's false, it will be a row
 */
export const AllSubscriptions = ({
    title="SUBSCRIPTIONS",
    subtitle="",
    subscriptions,
    showSingleTypeId=null,
    showSingleNonActive=false,
    showAmount=true,
    showNoSubsOfType=true,
    showDates=false,
    maxWidth="450px",
    border=true,
    hover=false,
    col=true,
    maxCard="unset"
}) => {

    const activeSubs = subscriptions?.filter((sub)=>sub?.subscription_status_id ===  1);
    const suspendedSubs = subscriptions?.filter((sub)=>sub?.subscription_status_id ===  2);
    const cancelledSubs = subscriptions?.filter((sub)=>sub?.subscription_status_id ===  3);
    const expiredSubs = subscriptions?.filter((sub)=>sub?.subscription_status_id ===  4);

    const [ override, setOverride]=useState(null);

    const handleOverrideClick=(status)=>{
        if(!override && override !== status) setOverride(status);
        else setOverride(null);
    }

    return (
        <div 
            style={{maxWidth: maxWidth}}
            className={`
                    ${styles["all-subs-wrapper"]}
                    ${styles[border ? "border" : ""]}
                `}
        >
            {title &&
                <p className={styles["title"]}>
                    SUBSCRIPTIONS
                </p>    
            }
            {subtitle &&
                <p className={styles["subtitle"]}>
                    {subtitle}
                </p>    
            }
            <div className={styles["main-body"]}>

                {showSingleTypeId ? 
                    <div>
                        {subscriptions?.filter((sub)=>sub.subscription_status_id === showSingleTypeId)?.map((sub)=>(
                            <p key={`subscription-${sub.id}`}>
                                <SubscriptionInfo 
                                    maxWidth={maxCard}
                                    subscription={sub}
                                    showDates={showDates}
                                    border={false}
                                />
                            </p>
                        ))}
                    </div>
                    :
                    <div className={`${col ? styles["col"] : styles["row"]}`}>
                        <div className={styles["each-type-wrapper"]}>
                            <SoftCard width={maxCard} hover={hover} border={false}>
                                <SoftCard.Header> 
                                    <span className={`
                                        ${styles["sub-status"]}
                                        ${styles["active"]}
                                    `}>
                                        <i className="far fa-lightbulb" style={{paddingLeft:"5px", paddingRight:"4px"}}/> 
                                            Active Subscriptions
                                    </span>
                                </SoftCard.Header>
                                {activeSubs?.length > 0 ? 
                                    <>
                                        {activeSubs?.map((sub, i)=>(
                                            <React.Fragment 
                                                key={`subscription-${sub.id}`}
                                            >
                                            <SubscriptionInfo
                                                maxWidth={maxCard} 
                                                subscription={sub}
                                                showDates={showDates}
                                                border={false}
                                            />
                                            {activeSubs?.length -1 && i !== activeSubs?.length - 1 ?
                                                <hr style={{maxWidth: maxWidth}} /> : ""
                                            }
                                            </React.Fragment>
                                        ))}
                                    </>
                                    :
                                    <p className={`
                                        ${styles["no-subs"]}
                                        ${styles[border ? "border" : ""]}
                                    `}>
                                        <span>
                                            No Active Subscriptions
                                        </span>
                                    </p>

                                }

                            </SoftCard>
                        </div>

                        {/* Suspended Subs */}
                        <div className={styles["each-type-wrapper"]}>
                            <SoftCard width={maxCard} hover={hover} border={false}>
                                <SoftCard.Header> 
                                    <span className={`
                                        ${styles["sub-status"]}
                                        ${styles["suspended"]}
                                    `}>
                                        <i className="far fa-lightbulb" style={{paddingLeft:"5px", paddingRight:"4px"}}/> 
                                            Suspended Subscriptions
                                    </span>
                                </SoftCard.Header>
                                {suspendedSubs?.length > 0 ?
                                    <>
                                        {showSingleNonActive && override !== "suspended" &&
                                            <>
                                                <SubscriptionInfo 
                                                    maxWidth={maxCard}
                                                    subscription={suspendedSubs[0]}
                                                    showDates={showDates}
                                                    key={`subscription-${suspendedSubs[0].id}`}
                                                    border={false}
                                                />
                                            </>
                                        }
                                        {((showSingleNonActive && override === "suspended") | !showSingleNonActive) &&
                                            <>
                                                {suspendedSubs?.map((sub, i)=>(
                                                    <React.Fragment key = {`suspended-subscription-${sub.id}`}>
                                                        <SubscriptionInfo 
                                                            maxWidth={maxCard}
                                                            subscription={sub}
                                                            showDates={showDates}
                                                            border={false}
                                                        />
                                                        {suspendedSubs?.length > 1 && i !== suspendedSubs?.length - 1 - 1 ?
                                                            <hr style={{maxWidth: maxWidth}} /> : ""
                                                        }
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        }
                                        {showSingleNonActive && showAmount && suspendedSubs?.length > 1 &&
                                            <p className={styles["other-totals"]}>
                                                {override==="suspended" ?
                                                    <span>
                                                        {suspendedSubs?.length} total suspended subscription{suspendedSubs?.length > 1 ? "s" : ""}
                                                    </span>
                                                :
                                                    <span>
                                                        + {suspendedSubs?.length - 1} other suspended subscription{suspendedSubs?.length - 1 > 1 ? "s" : ""}
                                                    </span>
                                                }
                                                <br />
                                                <Button onClick={()=>handleOverrideClick("suspended")}>
                                                    {override === "suspended" ? "Show Less" : "Show All"}
                                                </Button>
                                            </p>
                                        }
                                    </> 
                                    :
                                    <>
                                        {showNoSubsOfType ? 
                                            <p className={`
                                                ${styles["no-subs"]}
                                                ${styles[border ? "border" : ""]}
                                            `}>
                                                <span>
                                                    No Suspended  Subscriptions
                                                </span>
                                            </p> 
                                        : 
                                            ""
                                        }
                                    </>
                                }
                            </SoftCard>
                        </div>


                        {/* Cancelled Subs */}
                        <div className={styles["each-type-wrapper"]}>
                            <SoftCard width={maxCard} hover={hover} border={false}>
                                <SoftCard.Header> 
                                    <span className={`
                                        ${styles["sub-status"]}
                                        ${styles["cancelled"]}
                                    `}>
                                        <i className="far fa-lightbulb" style={{paddingLeft:"5px", paddingRight:"4px"}}/> 
                                            Cancelled Subscriptions
                                    </span>
                                </SoftCard.Header>
                                {cancelledSubs?.length > 0 ?
                                    <>
                                        {showSingleNonActive && override !== "cancelled" &&
                                            <>
                                                <SubscriptionInfo 
                                                    maxWidth={maxCard}
                                                    subscription={cancelledSubs[0]}
                                                    showDates={showDates}
                                                    key={`subscription-${cancelledSubs[0].id}`}
                                                    border={false}
                                                />
                                            </>
                                        }
                                        {((showSingleNonActive && override==="cancelled") || !showSingleNonActive) &&
                                            <>
                                                {cancelledSubs?.map((sub,i)=>(
                                                    <React.Fragment key={`cancelled-subscription-${sub.id}`}>
                                                        <SubscriptionInfo 
                                                            maxWidth={maxCard}
                                                            subscription={sub}
                                                            showDates={showDates}
                                                            border={false}
                                                        />
                                                        {cancelledSubs?.length > 1 && i !== cancelledSubs?.length - 1 ?
                                                            <hr style={{maxWidth: maxWidth}} /> : ""
                                                        }
                                                    </React.Fragment>
                                                ))}       
                                            </>
                                        }
                                        {showSingleNonActive &&showAmount && cancelledSubs?.length > 1 &&
                                            <p className={styles["other-totals"]}>
                                                {override==="cancelled" ?
                                                    <span>
                                                        {cancelledSubs?.length} total expired subscription{cancelledSubs?.length > 1 ? "s" : ""}
                                                    </span>
                                                :
                                                    <span>
                                                        + {cancelledSubs?.length - 1} other expired subscription{cancelledSubs?.length - 1 > 1 ? "s" : ""}
                                                    </span>
                                                }
                                                <br />
                                                <Button onClick={()=>handleOverrideClick("cancelled")}>
                                                    {override === "cancelled" ? "Show Less" : "Show All"}
                                                </Button>
                                            </p>
                                        }
                                    </>
                                    :
                                    <>
                                        {showNoSubsOfType ? 
                                            <p className={`
                                                ${styles["no-subs"]}
                                                ${styles[border ? "border" : ""]}
                                            `}>
                                                <span>
                                                    No Cancelled Subscriptions
                                                </span>
                                            </p> 
                                        : 
                                            ""
                                        }
                                    </>
                                }
                            </SoftCard>
                        </div>

                        {/* Expired Subs */}
                        <div className={styles["each-type-wrapper"]}>
                            <SoftCard width={maxCard} hover={hover} border={false}>
                                <SoftCard.Header> 
                                    <span className={`
                                        ${styles["sub-status"]}
                                        ${styles["expired"]}
                                    `}>
                                        <i className="far fa-lightbulb" style={{paddingLeft:"5px", paddingRight:"4px"}}/> 
                                            Expired Subscriptions
                                    </span>
                                </SoftCard.Header>
                                {expiredSubs?.length > 0 ?
                                    <>
                                        {showSingleNonActive && override!=="expired" &&
                                            <>
                                                <SubscriptionInfo 
                                                    maxWidth={maxCard}
                                                    subscription={expiredSubs[0]}
                                                    showDates={showDates}
                                                    key={`subscription-${expiredSubs[0].id}`}
                                                    border={false}
                                                />
                                            </>
                                        }
                                        {((showSingleNonActive && override === "expired") || !showSingleNonActive) &&
                                            <>
                                                {expiredSubs?.map((sub,i)=>(
                                                    <React.Fragment key={`expired-subscription-${sub.id}`}>
                                                        <SubscriptionInfo 
                                                            maxWidth={maxCard}
                                                            subscription={sub}
                                                            showDates={showDates}
                                                            key={`subscription-${sub.id}`}
                                                            border={false}
                                                        />
                                                        {expiredSubs?.length > 1 && i !== expiredSubs?.length - 1 ?
                                                            <hr style={{maxWidth: maxWidth}} /> : ""
                                                        }
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        }
                                        {showSingleNonActive && showAmount && expiredSubs?.length > 1 &&
                                            <p className={styles["other-totals"]}>
                                                {override==="expired" ?
                                                    <span>
                                                        {expiredSubs?.length} total expired subscription{expiredSubs?.length > 1 ? "s" : ""}
                                                    </span>
                                                :
                                                    <span>
                                                        + {expiredSubs?.length - 1} other expired subscription{suspendedSubs?.length - 1 > 1 ? "s" : ""}
                                                    </span>
                                                }
                                                <br />
                                                <Button onClick={()=>handleOverrideClick("expired")}>
                                                    {override === "expired" ? "Show Less" : "Show All"}
                                                </Button>
                                            </p>
                                        }
                                    </>
                                    :
                                    <>
                                        {showNoSubsOfType ? 
                                            <p className={`
                                                ${styles["no-subs"]}
                                                ${styles[border ? "border" : ""]}
                                            `}>
                                                <span>
                                                    No Expired Subscriptions
                                                </span>
                                            </p> 
                                        : 
                                            ""
                                        }
                                    </>
                                }
                            </SoftCard>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}